import React, { createContext, useState, useEffect, useContext } from 'react';
import { DeSoIdentityContext } from "react-deso-protocol";
import { checkPartyAccessGroups, createAccessGroup, getAllAccessGroups, getPaginatedAccessGroupMembers, identity } from 'deso-protocol';

export const AccessGroupsContext = createContext();

export const AccessGroupsProvider = ({ children }) => {
  const { currentUser } = useContext(DeSoIdentityContext);
  const [accessGroups, setAccessGroups] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [accessGroupMembers, setAccessGroupMembers] = useState({});

  const triggerRefreshAccessGroups = () => {
    fetchAccessGroups();
  }

  const fetchAccessGroups = async () => {
    try {
      if (currentUser) {
        const groups = await getAllAccessGroups({ PublicKeyBase58Check: currentUser.PublicKeyBase58Check });
        setAccessGroups(groups);

        if (!groups || !groups.AccessGroupsOwned.some(group => group.AccessGroupKeyName === "default-key")) {
          console.log("[AccessGroups.js] fetchAccessGroups, no default-key found:", currentUser, groups);
          console.log("[AccessGroups.js] WE NEED TO CONFIGURE USER DEFAULT ACCESS KEY");
          const defaultKey = await identity.accessGroupStandardDerivation("default-key");
          console.log("[AccessGroups.js] fetchAccessGroups, defaultKey:", defaultKey);
          const newDefaultKey = await createAccessGroup({
            AccessGroupOwnerPublicKeyBase58Check: currentUser.PublicKeyBase58Check,
            AccessGroupKeyName: defaultKey.AccessGroupKeyName,
            AccessGroupPublicKeyBase58Check: defaultKey.AccessGroupPublicKeyBase58Check,
          });
          console.log("[AccessGroups.js] fetchAccessGroups, newDefaultKey:", newDefaultKey);
          // Refetch access groups after creating the default key
          fetchAccessGroups();
          return;
        }
      } else {
        setAccessGroups(null);
      }
    } catch (error) {
      console.error('[AccessGroups.jsx] Error fetching access groups:', error);
    }
  };

  const fetchAccessGroupMembers = async () => {
    if (!currentUser || !accessGroups) return;

    try {
      console.log("[AccessGroups.js] fetchAccessGroupMembers, accessGroups:",accessGroups);
      accessGroups.Members = [];
      if (accessGroups.AccessGroupsMember) {
        for (const group of accessGroups.AccessGroupsMember) {
          const paginatedMembers = await getPaginatedAccessGroupMembers({
            AccessGroupOwnerPublicKeyBase58Check: group.AccessGroupOwnerPublicKeyBase58Check,
            AccessGroupKeyName: group.AccessGroupKeyName,
            MaxMembersToFetch: 200,
          });
          accessGroups.Members[group.AccessGroupKeyName] = paginatedMembers.AccessGroupMembersBase58Check;
        }
      }
      //setAccessGroupMembers(newMembers);
    } catch (error) {
      console.error('[AccessGroups.jsx] Error fetching access group members:', error);
    }
  };

  useEffect(() => {
    setAccessGroups(null);
    fetchAccessGroups();
  }, [currentUser]);

  useEffect(() => {
    fetchAccessGroupMembers();
  }, [accessGroups]);

  useEffect(() => {
    if (currentUser && accessGroups) {
      // Poll access group members less frequently
      const membersPollingInterval = setInterval(fetchAccessGroups, 300000);

      return () => {
        clearInterval(membersPollingInterval);
      };
    }
  }, [currentUser, accessGroups]);

  return (
    <AccessGroupsContext.Provider value={{ accessGroups, isLoading, triggerRefreshAccessGroups }}>
      {children}
    </AccessGroupsContext.Provider>
  );
};

const getAccessGroup = (accessGroupPublicKeyBase58Check, accessGroups) => {
  // Destructure the arrays from accessGroups
  const { AccessGroupsMember = [], AccessGroupsOwned = [] } = accessGroups;

  // Combine both arrays for a single search
  const allAccessGroups = [...AccessGroupsMember, ...AccessGroupsOwned];

  // Find the group that matches the given AccessGroupPublicKeyBase58Check
  const matchingGroup = allAccessGroups.find(
    (group) => group.AccessGroupPublicKeyBase58Check === accessGroupPublicKeyBase58Check
  );

  // Return the AccessGroupKeyName or null if not found
  return matchingGroup ? matchingGroup : null;
};